'use client';

import React from 'react';
import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Tr,
  Icon,
  HStack,
  Stack,
  Box,
  Text,
  IconButton,
  Button,
  Link,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { IconType } from 'react-icons';
import { TbTrash } from 'react-icons/tb';
import { isHomeOwner } from '@habity/services';
import { NestedHome } from '@habity/types';
import { useUser } from '~/hooks/useUser';
import { isAdmin, isBroker } from '~/lib/roles';
import { useAppStore } from '~/store/useAppStore';
import { HabityTooltip } from '../tooltips';
import { ICONS } from '~/constants';
import { FEATURES } from '@habity/constants';
import { getFeatureButtonIcon } from '~/constants';
import { FeatureButtonType } from '@habity/types';
import { LuChevronDown } from 'react-icons/lu';
import { Menu, MenuButton, MenuList, MenuItem } from '@chakra-ui/react';
import { capitalizeFirstLetter } from '@habity/utils';

export type TableDataItem = {
  icon?: IconType;
  label?: string;
  value: string | number;
  id?: string;
  comment?: string;
  externalLink?: string;
};

// Utility function to detect URLs and render them as clickable links
const renderValueWithLinks = (value: string | number, fontSize: string) => {
  const stringValue = String(value);

  // URL regex pattern to detect both http/https URLs and www URLs
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;

  // Split the text by URLs to handle mixed content
  const parts = stringValue.split(urlRegex);

  return (
    <React.Fragment>
      {parts.map((part, index) => {
        // Check if this part is a URL
        if (urlRegex.test(part)) {
          // Add https:// protocol if the URL starts with www
          const href = part.startsWith('www.') ? `https://${part}` : part;

          return (
            <Link
              key={index}
              href={href}
              color="blue.500"
              textDecoration="underline"
              isExternal
              fontSize={fontSize}
            >
              {part}
            </Link>
          );
        } else {
          // Regular text
          return (
            <Text as="span" key={index} fontSize={fontSize}>
              {capitalizeFirstLetter(part)}
            </Text>
          );
        }
      })}
    </React.Fragment>
  );
};

interface ProfileTableProps {
  data: TableDataItem[];
  collapseAfter?: number;
  collapsedHeight?: number;
  home?: NestedHome;
  handleAdd?: (id: string) => void;
  handleRemove?: (id: string) => void;
  isEditable?: boolean;
  fontSize?: string;
}

export const ProfileTable = ({
  home,
  data,
  handleAdd,
  handleRemove,
  isEditable = false,
  fontSize = 'sm',
}: ProfileTableProps) => {
  const [availableFeatures, setAvailableFeatures] = useState<
    FeatureButtonType[]
  >([]);

  useEffect(() => {
    const existingFeatureKeys = data
      .filter((d) => d.id)
      .map((d) => d.id as string);
    const _availableFeatures = FEATURES.filter(
      (feature) => !existingFeatureKeys.includes(feature.key)
    );
    setAvailableFeatures(_availableFeatures);
  }, [data]);

  const [isAddingFeature, setIsAddingFeature] = useState<boolean>(false);
  const [selectedFeatureToAdd, setSelectedFeatureToAdd] =
    useState<FeatureButtonType | null>(null);

  const onStartAddingFeature = useCallback(() => {
    setIsAddingFeature(true);
    setSelectedFeatureToAdd(
      availableFeatures.length > 0 ? availableFeatures[0] : null
    );
  }, [availableFeatures]);

  const onAddFeature = () => {
    if (selectedFeatureToAdd) {
      handleAdd?.(selectedFeatureToAdd?.key);
    }
    setIsAddingFeature(false);
    setSelectedFeatureToAdd(null);
  };

  const { user } = useUser();

  return (
    <TableContainer>
      {/* Mobile: Stack layout like ParagraphList */}
      <Box display={{ base: 'block', md: 'none' }} px="0" py="4">
        {data.map((item: TableDataItem, index: number) => {
          return (
            <Stack key={index} mb="6">
              {item.label && (
                <HStack spacing="2">
                  {item.icon && <Icon as={item.icon} boxSize="5" />}
                  <Text size={fontSize} fontWeight="bold">
                    {item.label}
                  </Text>
                  {isEditable &&
                    handleRemove &&
                    home &&
                    (isAdmin(user) ||
                      isBroker(user) ||
                      isHomeOwner(home, user)) && (
                      <HabityTooltip
                        label={`Ta bort ${item.label} från fördelar`}
                      >
                        <IconButton
                          variant="outline"
                          aria-label="Ta bort"
                          size="xs"
                          icon={<TbTrash size="15" />}
                          onClick={() => item.id && handleRemove(item.id)}
                        />
                      </HabityTooltip>
                    )}
                </HStack>
              )}
              <Text size={fontSize} whiteSpace="pre-line">
                {renderValueWithLinks(item.value, fontSize)}
              </Text>
              {item.comment && (
                <Text size={fontSize} whiteSpace="pre-line">
                  {item.comment}
                </Text>
              )}
            </Stack>
          );
        })}
      </Box>

      {/* Desktop: Table layout */}
      <Table variant="unstyled" backgroundColor="transparent" mb="6" display={{ base: 'none', md: 'table' }}>
        <Tbody>
          {isAddingFeature && selectedFeatureToAdd && (
            <AddFeatureRow
              selectedFeature={selectedFeatureToAdd}
              setSelectedFeature={setSelectedFeatureToAdd}
              availableFeatures={availableFeatures}
            />
          )}
          {data.map((item: TableDataItem, index: number) => {
            return (
              <Tr verticalAlign="top" key={index}>
                {item.label && (
                  <Td px="0">
                    <HStack spacing="2">
                      {item.icon && <Icon as={item.icon} boxSize="5" />}{' '}
                      <Text size={fontSize} fontWeight="bold">
                        {item.label}
                      </Text>
                      {isEditable &&
                        handleRemove &&
                        home &&
                        (isAdmin(user) ||
                          isBroker(user) ||
                          isHomeOwner(home, user)) && (
                          <HabityTooltip
                            label={`Ta bort ${item.label} från fördelar`}
                          >
                            <IconButton
                              variant="outline"
                              aria-label="Ta bort"
                              size="xs"
                              icon={<TbTrash size="15" />}
                              onClick={() => item.id && handleRemove(item.id)}
                            />
                          </HabityTooltip>
                        )}
                    </HStack>
                  </Td>
                )}
                <Td>
                  <Text size={fontSize} whiteSpace="pre-line">
                    {renderValueWithLinks(item.value, fontSize)}
                  </Text>
                </Td>

                {item.comment && (
                  <Td>
                    <Text size={fontSize} whiteSpace="pre-line">
                      {item.comment}
                    </Text>
                  </Td>
                )}
              </Tr>
            );
          })}
        </Tbody>
      </Table>

      {/* Add/remove features */}
      <HStack>
        {isEditable && !isAddingFeature && (
          <Button
            variant="outline"
            leftIcon={<Icon as={ICONS.EDIT_HOME} />}
            onClick={onStartAddingFeature}
          >
            Lägg till fördel
          </Button>
        )}
        {isEditable && isAddingFeature && (
          <Button
            variant="outline"
            leftIcon={<Icon as={ICONS.SAVE} />}
            onClick={onAddFeature}
          >
            Spara
          </Button>
        )}
      </HStack>
    </TableContainer>
  );
};

interface AddFeatureRowProps {
  selectedFeature: FeatureButtonType;
  setSelectedFeature: (feature: FeatureButtonType) => void;
  availableFeatures: Array<FeatureButtonType>;
}

function AddFeatureRow({
  selectedFeature,
  setSelectedFeature,
  availableFeatures,
}: AddFeatureRowProps) {
  const isMobile = useAppStore((state) => state.isMobile);
  return (
    <Tr verticalAlign="top">
      <Td>
        <HStack spacing="2">
          {<Icon as={getFeatureButtonIcon(selectedFeature.key)} boxSize="5" />}{' '}
          <Text
            fontWeight="bold"
            whiteSpace={isMobile ? 'pre-line' : 'inherit'}
          >
            {selectedFeature.title}
          </Text>
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Visa funktioner"
              size="xs"
              icon={<LuChevronDown size="15" />}
              variant="outline"
            />
            <MenuList zIndex="4">
              {availableFeatures
                .sort((a, b) => a.title.localeCompare(b.title))
                .map((feature) => (
                  <MenuItem
                    key={feature.key}
                    onClick={() => setSelectedFeature(feature)}
                  >
                    <HStack spacing="2">
                      {
                        <Icon
                          as={getFeatureButtonIcon(feature.key)}
                          boxSize="5"
                        />
                      }{' '}
                      <Text
                        fontWeight="bold"
                        whiteSpace={isMobile ? 'pre-line' : 'inherit'}
                      >
                        {feature.title}
                      </Text>
                    </HStack>
                  </MenuItem>
                ))}
            </MenuList>
          </Menu>
        </HStack>
      </Td>
      <Td>
        <Text whiteSpace="pre-line">{selectedFeature.description}</Text>
      </Td>
    </Tr>
  );
}
